import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { productApi } from '#services/product';
import {
  ALL_TIME_RANGE_PRESET,
  TimeRangeValue,
  buildTimeRange,
} from '#components/time-range.utils';
import { revertAll } from '../actions';

interface ProductListState {
  filter: string;
  dateRange: TimeRangeValue;
  filterKey: string;
  headerFilters: Record<string, Record<string, boolean>>;
  order: string;
  orderBy: string;
  pageIndex: number;
  rows: unknown[];
  rowCount: number;
  pageSize: number;
  vendors: Record<string, unknown>;
  selected: Array<string | number>;
}

interface GetProductsPayload {
  page: number;
  rows: unknown[];
  total: number;
  vendors: Record<string, unknown>;
}

interface GetProductsFulfilledAction {
  payload: unknown;
  meta: { arg: { endpointName?: string } };
}

const initialState: ProductListState = {
  filter: '',
  dateRange: buildTimeRange(ALL_TIME_RANGE_PRESET),
  filterKey: 'name',
  headerFilters: {},
  order: 'asc',
  orderBy: 'name',
  pageIndex: 0,
  rows: [],
  rowCount: 0,
  pageSize: 30,
  vendors: {},
  selected: [],
};

const handleGetProductsFulfilled = (
  state: ProductListState,
  action: GetProductsFulfilledAction,
) => {
  const {
    page,
    rows,
    total,
    vendors,
  } = action.payload as GetProductsPayload;
  state.pageIndex = page;
  state.rows = rows;
  state.rowCount = total;
  state.vendors = vendors;

  if (action.meta.arg.endpointName === 'deleteProducts') {
    state.selected = [];
  }
};

export const productListSlice = createSlice({
  name: 'productList',
  initialState,
  reducers: {
    setDateRange(state, action: PayloadAction<TimeRangeValue>) {
      state.dateRange = action.payload;
      state.pageIndex = 0;
    },
    setFilter(state, action: PayloadAction<string>) {
      state.filter = action.payload;
    },
    setFilterKey(state, action: PayloadAction<string>) {
      state.filterKey = action.payload;
    },
    setHeaderFilters(state, action: PayloadAction<Record<string, Record<string, boolean>>>) {
      state.headerFilters = action.payload;
    },
    setOrder(state, action: PayloadAction<string>) {
      state.order = action.payload;
    },
    setOrderBy(state, action: PayloadAction<string>) {
      state.orderBy = action.payload;
    },
    setPageIndex(state, action: PayloadAction<number>) {
      state.pageIndex = action.payload;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      state.pageIndex = 0;
    },
    setRows(state, action: PayloadAction<unknown[]>) {
      state.rows = action.payload;
    },
    setRowCount(state, action: PayloadAction<number>) {
      state.rowCount = action.payload;
    },
    setSelected(state, action: PayloadAction<Array<string | number>>) {
      const { payload } = action;
      state.selected = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(revertAll, () => initialState)
      .addMatcher(productApi.endpoints.getProducts.matchFulfilled, handleGetProductsFulfilled)
      .addMatcher(productApi.endpoints.deleteProducts.matchFulfilled, handleGetProductsFulfilled)
      .addMatcher(productApi.endpoints.uploadProducts.matchFulfilled, handleGetProductsFulfilled)
      .addMatcher(productApi.endpoints.bulkEditProducts.matchFulfilled, handleGetProductsFulfilled);
  },
});

export const {
  setDateRange,
  setFilter,
  setFilterKey,
  setHeaderFilters,
  setOrder,
  setOrderBy,
  setPageIndex,
  setPageSize,
  setRows,
  setRowCount,
  setSelected,
} = productListSlice.actions;

export default productListSlice.reducer;
